import { isInvalidPriceRange } from "~/hec/utils/price-range";

export const NEGATIVE_INFINITY = "-inf";
export const INIFINITY = "inf";

// Shown under the price inputs when a user enters a backwards range. Kept in
// one place so the message stays consistent with any test asserting on it.
export const INVALID_PRICE_RANGE_MESSAGE =
  "Minimum price can't be higher than the maximum.";

export const handlePriceInputChangeHelper = (
  type: "min" | "max",
  value: string,
  priceRange: { min?: string; max?: string },
  selectedCioFacets: any,
  setPriceRange: (updater: (prev: any) => any) => void,
  dispatchSelectedFacets: (action: any) => void,
  setPriceError?: (message: string | null) => void,
) => {
  setPriceRange((prev: any) => ({
    ...prev,
    [type]: value || undefined,
  }));

  const otherType = type === "min" ? "max" : "min";
  const otherValue = priceRange?.[otherType] || "";
  const newSelectedFacets = { ...selectedCioFacets };
  if ("price" in newSelectedFacets) {
    delete newSelectedFacets.price;
  }
  if (
    (type === "min" && !value && !otherValue) ||
    (type === "max" && !value && !otherValue)
  ) {
    setPriceError?.(null);
    dispatchSelectedFacets({
      type: "SET_CIO_SELECTED_FACETS",
      payload: newSelectedFacets,
    });
    return;
  }

  const minValue =
    type === "min"
      ? value === ""
        ? NEGATIVE_INFINITY
        : value
      : priceRange?.min || NEGATIVE_INFINITY;
  const maxValue =
    type === "max"
      ? value === ""
        ? INIFINITY
        : value
      : priceRange?.max || INIFINITY;

  // A backwards range (min > max) would zero out CIO results. Surface an inline
  // error under the inputs and skip the dispatch so the malformed filter never
  // reaches the URL / query. The `-inf` / `inf` sentinels parse to null, so a
  // one-sided ("Under $X" / "Over $X") range never trips this.
  if (isInvalidPriceRange(minValue, maxValue)) {
    setPriceError?.(INVALID_PRICE_RANGE_MESSAGE);
    return;
  }

  setPriceError?.(null);
  dispatchSelectedFacets({
    type: "SET_CIO_SELECTED_FACETS",
    payload: {
      ...newSelectedFacets,
      price: [
        {
          value: `${minValue} - ${maxValue}`,
          display_name: `${minValue} - ${maxValue}`,
        },
      ],
    },
  });
};

export const getNewSelectedFilters = (
  selectedCioFacets: any,
  category: string,
  option: { value: string; display_name: string; count: number },
) => {
  const categoryExists = selectedCioFacets[category];
  const newCategoryValues = categoryExists?.some(
    (item: any) => item.value === option.value,
  )
    ? categoryExists.filter((item: any) => item.value !== option.value)
    : [
        ...(categoryExists || []),
        { value: option.value, display_name: option.display_name },
      ];

  const newSelectedFilters = {
    ...selectedCioFacets,
    ...(newCategoryValues.length > 0 ? { [category]: newCategoryValues } : {}),
  };

  if (newCategoryValues.length === 0) {
    delete newSelectedFilters[category];
  }

  return newSelectedFilters;
};
